<template>
  <div class="advert-config-dialog">
    <el-dialog
      :title="dialogTitle"
      :visible.sync="localVisible"
      :modal-append-to-body="true"
      :append-to-body="true"
      width="72%"
      top="7vh"
      @open="handleOpen"
      @closed="handleClosed"
      :close-on-click-modal="false"
    >
      <div class="config-header">
        <div class="config-summary">
          <span class="summary-label">{{ summaryLabel }}</span>
          <span class="summary-value">{{ summaryValue }}</span>
        </div>
        <el-alert
          v-if="mode === 'group'"
          :title="$t('equipmentAdvertConfig.groupOverwriteTip')"
          type="warning"
          :closable="false"
          show-icon
        />
      </div>

      <div v-if="mode === 'device'" class="config-toolbar">
        <el-radio-group v-model="activeView" size="small">
          <el-radio-button label="configured">{{ $t("equipmentAdvertConfig.configuredAds") }}</el-radio-button>
          <el-radio-button label="library">{{ $t("equipmentAdvertConfig.availableAds") }}</el-radio-button>
        </el-radio-group>
      </div>

      <div class="config-toolbar">
        <el-form :inline="true" size="small" class="toolbar-form">
          <el-form-item>
            <el-input
              v-model="search.head"
              :placeholder="$t('searchAdverts.title')"
              clearable
              @keyup.enter.native="handleSearch"
            ></el-input>
          </el-form-item>
          <el-form-item>
            <el-input
              v-model="search.userName"
              :placeholder="$t('searchAdverts.userName')"
              clearable
              @keyup.enter.native="handleSearch"
            ></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">{{ $t("common.search") }}</el-button>
            <el-button icon="el-icon-refresh" @click="handleReset">{{ $t("common.reset") }}</el-button>
          </el-form-item>
        </el-form>
      </div>

      <el-table
        v-if="mode === 'device' && activeView === 'configured'"
        ref="configuredTable"
        v-loading="loadingConfigured"
        :data="configuredAds"
        :empty-text="$t('common.noData')"
        :element-loading-text="$t('common.loading')"
        border
        height="460"
        @selection-change="handleConfiguredSelectionChange"
      >
        <el-table-column type="selection" width="55" align="center"></el-table-column>
        <el-table-column :label="$t('adverts.showContent')" width="180" align="center">
          <template slot-scope="scope">
            <div class="advert-preview">
              <el-image
                v-if="scope.row.type === 0"
                :src="scope.row.url"
                :preview-src-list="[scope.row.url]"
                fit="cover"
                class="advert-preview__image"
              ></el-image>
              <video v-else :src="scope.row.url" controls class="advert-preview__video"></video>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="head" :label="$t('adverts.title')" min-width="180"></el-table-column>
        <el-table-column prop="userName" :label="$t('adverts.userName')" min-width="160"></el-table-column>
        <el-table-column prop="createTime" :label="$t('adverts.createTime')" width="180"></el-table-column>
      </el-table>

      <el-table
        v-else
        ref="libraryTable"
        v-loading="loadingLibrary"
        :data="visibleLibraryAds"
        :empty-text="$t('common.noData')"
        :element-loading-text="$t('common.loading')"
        border
        height="460"
        row-key="id"
        @selection-change="handleLibrarySelectionChange"
      >
        <el-table-column type="selection" width="55" align="center" reserve-selection></el-table-column>
        <el-table-column :label="$t('adverts.showContent')" width="180" align="center">
          <template slot-scope="scope">
            <div class="advert-preview">
              <el-image
                v-if="scope.row.type === 0"
                :src="scope.row.url"
                :preview-src-list="[scope.row.url]"
                fit="cover"
                class="advert-preview__image"
              ></el-image>
              <video v-else :src="scope.row.url" controls class="advert-preview__video"></video>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="head" :label="$t('adverts.title')" min-width="180"></el-table-column>
        <el-table-column prop="userName" :label="$t('adverts.userName')" min-width="160"></el-table-column>
        <el-table-column prop="createTime" :label="$t('adverts.createTime')" width="180"></el-table-column>
      </el-table>

      <el-pagination
        v-if="activeView !== 'configured' || mode === 'group'"
        class="config-pagination"
        background
        layout="total,sizes,prev,pager,next,jumper"
        :current-page="page.pageIndex"
        :page-size="page.pageSize"
        :total="page.totalSize"
        @size-change="handleSizeChange"
        @current-change="loadLibrary"
      ></el-pagination>

      <div slot="footer" class="dialog-footer">
        <el-button @click="emitClose(false)">{{ $t("common.cancel") }}</el-button>
        <el-button
          v-if="mode === 'device' && activeView === 'configured'"
          type="danger"
          :disabled="configuredSelection.length === 0 || saving"
          @click="handleRemove"
        >
          {{ $t("equipmentAdvertConfig.removeSelected") }}
        </el-button>
        <el-button
          v-else-if="mode === 'device'"
          type="primary"
          :disabled="librarySelectionIds.length === 0 || saving"
          @click="handleAdd"
        >
          {{ $t("equipmentAdvertConfig.addSelected") }}
        </el-button>
        <el-button
          v-else
          type="primary"
          :disabled="librarySelectionIds.length === 0 || saving"
          @click="handleBatchSave"
        >
          {{ $t("equipmentAdvertConfig.batchSave") }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "AdvertConfigDialog",
  props: {
    visible: Boolean,
    mode: {
      type: String,
      default: "device"
    },
    equipmentNo: {
      type: String,
      default: ""
    },
    teamId: {
      type: String,
      default: ""
    },
    targetName: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      loadingConfigured: false,
      loadingLibrary: false,
      saving: false,
      activeView: "configured",
      configuredAds: [],
      libraryAds: [],
      configuredSelection: [],
      librarySelection: [],
      search: {
        head: "",
        userName: ""
      },
      page: {
        pageIndex: 1,
        pageSize: 10,
        totalSize: 0
      }
    };
  },
  computed: {
    localVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.emitClose(false);
        }
      }
    },
    dialogTitle() {
      return this.mode === "device"
        ? this.$t("equipmentAdvertConfig.deviceTitle")
        : this.$t("equipmentAdvertConfig.groupTitle");
    },
    summaryLabel() {
      return this.mode === "device"
        ? this.$t("equipmentAdvertConfig.machineNo")
        : this.$t("equipmentAdvertConfig.groupName");
    },
    summaryValue() {
      return this.mode === "device" ? this.equipmentNo : this.targetName;
    },
    configuredIdSet() {
      return new Set(this.configuredAds.map(item => item.id));
    },
    visibleLibraryAds() {
      if (this.mode === "group") {
        return this.libraryAds;
      }
      return this.libraryAds.filter(item => !this.configuredIdSet.has(item.id));
    },
    librarySelectionIds() {
      return this.librarySelection.map(item => item.id);
    }
  },
  methods: {
    handleOpen() {
      this.activeView = this.mode === "device" ? "configured" : "library";
      this.resetSelections();
      if (this.mode === "device") {
        Promise.resolve(this.loadConfigured()).finally(() => {
          this.loadLibrary(1);
        });
      } else {
        this.loadLibrary(1);
      }
    },
    handleClosed() {
      this.resetState();
    },
    resetState() {
      this.activeView = "configured";
      this.search.head = "";
      this.search.userName = "";
      this.page.pageIndex = 1;
      this.page.pageSize = 10;
      this.page.totalSize = 0;
      this.configuredAds = [];
      this.libraryAds = [];
      this.resetSelections();
    },
    resetSelections() {
      this.configuredSelection = [];
      this.librarySelection = [];
    },
    emitClose(refresh) {
      this.$emit("close", false, refresh);
    },
    handleSearch() {
      this.loadLibrary(1);
    },
    handleReset() {
      this.search.head = "";
      this.search.userName = "";
      this.loadLibrary(1);
    },
    handleSizeChange(pageSize) {
      this.page.pageSize = pageSize;
      this.loadLibrary(1);
    },
    loadConfigured() {
      if (!this.equipmentNo) {
        this.configuredAds = [];
        return Promise.resolve();
      }
      this.loadingConfigured = true;
      return this.$api
        .request(this.$url.UserEquipmentAdvert.detail, this.$method.get, {
          equipmentNo: this.equipmentNo
        })
        .then(res => {
          if (res.code === 0) {
            this.configuredAds = res.data.ads || [];
          }
        })
        .finally(() => {
          this.loadingConfigured = false;
        });
    },
    loadLibrary(pageIndex) {
      this.loadingLibrary = true;
      this.page.pageIndex = pageIndex || this.page.pageIndex;
      return this.$api
        .request(this.$url.Adverts.page, this.$method.get, {
          head: this.search.head,
          userName: this.search.userName,
          pageIndex: this.page.pageIndex,
          pageSize: this.page.pageSize
        })
        .then(res => {
          if (res.code === 0) {
            this.libraryAds = res.data.records;
            this.page.totalSize = Number(res.data.total);
          }
        })
        .finally(() => {
          this.loadingLibrary = false;
        });
    },
    handleConfiguredSelectionChange(rows) {
      this.configuredSelection = rows;
    },
    handleLibrarySelectionChange(rows) {
      this.librarySelection = rows;
    },
    confirmAction(message, handler) {
      this.$confirm(message, this.$t("common.tip"), {
        confirmButtonText: this.$t("common.definite"),
        cancelButtonText: this.$t("common.cancel"),
        type: "warning"
      }).then(handler);
    },
    handleAdd() {
      this.confirmAction(this.$t("equipmentAdvertConfig.confirmAdd"), () => {
        this.saving = true;
        this.$api
          .request(this.$url.UserEquipmentAdvert.add, this.$method.post, {
            equipmentNo: this.equipmentNo,
            adIds: this.librarySelectionIds
          })
          .then(res => {
            if (res.code === 0) {
              this.$message.success(res.msg || this.$t("common.success"));
              this.resetSelections();
              return this.loadConfigured().then(() => this.loadLibrary(this.page.pageIndex));
            }
            this.$message.error(res.msg || this.$t("common.error"));
          })
          .catch(err => {
            this.$message.error((err && err.msg) || this.$t("common.error"));
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
    handleRemove() {
      this.confirmAction(this.$t("equipmentAdvertConfig.confirmRemove"), () => {
        this.saving = true;
        this.$api
          .request(this.$url.UserEquipmentAdvert.remove, this.$method.delete, {
            equipmentNo: this.equipmentNo,
            adIds: this.configuredSelection.map(item => item.id)
          })
          .then(res => {
            if (res.code === 0) {
              this.$message.success(res.msg || this.$t("common.success"));
              this.configuredSelection = [];
              return this.loadConfigured().then(() => this.loadLibrary(this.page.pageIndex));
            }
            this.$message.error(res.msg || this.$t("common.error"));
          })
          .catch(err => {
            this.$message.error((err && err.msg) || this.$t("common.error"));
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
    handleBatchSave() {
      this.confirmAction(this.$t("equipmentAdvertConfig.confirmBatchSave"), () => {
        this.saving = true;
        this.$api
          .request(this.$url.UserEquipmentAdvert.batchSave, this.$method.put, {
            userEquipmentTeamId: this.teamId,
            adIds: this.librarySelectionIds
          })
          .then(res => {
            if (res.code === 0) {
              this.$message.success(res.msg || this.$t("common.success"));
              this.emitClose(true);
              return;
            }
            this.$message.error(res.msg || this.$t("common.error"));
          })
          .catch(err => {
            this.$message.error((err && err.msg) || this.$t("common.error"));
          })
          .finally(() => {
            this.saving = false;
          });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.config-header {
  margin-bottom: 16px;
}

.config-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
}

.summary-label {
  color: #909399;
}

.summary-value {
  color: #303133;
  font-weight: 500;
}

.config-toolbar {
  margin-bottom: 16px;
}

.toolbar-form {
  display: flex;
  flex-wrap: wrap;
}

.advert-preview {
  display: flex;
  justify-content: center;
  align-items: center;
}

.advert-preview__image,
.advert-preview__video {
  width: 120px;
  height: 96px;
  border-radius: 8px;
  object-fit: cover;
}

.config-pagination {
  margin-top: 16px;
  text-align: right;
}
</style>
